import React, { useContext } from 'react';
import { MainContext } from '../../context/MainContext';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import AvTimerOutlinedIcon from '@mui/icons-material/AvTimerOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';
import PoolOutlinedIcon from '@mui/icons-material/PoolOutlined';
import HeatPumpOutlinedIcon from '@mui/icons-material/HeatPumpOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import SettingsInputHdmiOutlinedIcon from '@mui/icons-material/SettingsInputHdmiOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import StayCurrentLandscapeOutlinedIcon from '@mui/icons-material/StayCurrentLandscapeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';

function padToTwoDigits(n) {
  return n.toString().padStart(2, "0");
}

function toHoursAndMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${padToTwoDigits(hours)}:${padToTwoDigits(minutes)}`;
}

export default function DashboardControl() {
  const state = useContext(MainContext).state;
  const r2 = state.R2.split(",");
  const r3 = state.R3.split(",");
  const r4 = state.R4.split(",");

  const ICON_COLORS = {
    heat: "#FF7043",        // orange / heat
    cool: "#4FC3F7",        // light blue
    water: "#29B6F6",       // pool / water
    time: "#BA68C8",        // timers
    power: "#FFD54F",       // electrical
    speed: "#81C784",       // motion / speed
    level: "#90A4AE",       // neutral / measurement
  };

  // GENERAL
  const General = [
    {
      label: "Heat mode",
      icon: <LocalFireDepartmentOutlinedIcon sx={{ color: ICON_COLORS.heat }} />,
      value: [
        "Off", "Standby", "Heat mix", "Heat test", "Heat",
        "Cool mix", "Cool test", "Cool"
      ][Number(r4[5])] ?? "Off"
    },
    { label: "Pump idle timer", icon: <AvTimerOutlinedIcon sx={{ color: ICON_COLORS.time }} />, value: new Date(r4[6] * 1000).toISOString().slice(11, 19) },
    { label: "Pump run timer", icon: <AvTimerOutlinedIcon sx={{ color: ICON_COLORS.time }} />, value: new Date(r4[7] * 1000).toISOString().slice(11, 19) },
    { label: "Heat source off time", icon: <TimerOffOutlinedIcon sx={{ color: ICON_COLORS.time }} />, value: r4[22] },
    { label: "Adaptive pool hys", icon: <PoolOutlinedIcon sx={{ color: ICON_COLORS.water }} />, value: (r4[8] / 10).toFixed(1) },
    { label: "Adaptive heater hys", icon: <HeatPumpOutlinedIcon sx={{ color: ICON_COLORS.water }} />, value: (r4[9] / 10).toFixed(1) },
    { label: "Element offset", icon: <WavesOutlinedIcon sx={{ color: ICON_COLORS.cool }} />, value: (r4[18] / 10).toFixed(1) },
    { label: "HP heat offset", icon: <LocalFireDepartmentOutlinedIcon sx={{ color: ICON_COLORS.heat }} />, value: (r4[19] / 10).toFixed(1) },
    { label: "HP cool offset", icon: <AcUnitOutlinedIcon sx={{ color: ICON_COLORS.cool }} />, value: (r4[21] / 10).toFixed(1) },
    { label: "Thermal cutout", icon: <LocalFireDepartmentOutlinedIcon sx={{ color: ICON_COLORS.heat }} />, value: r4[14] === "1" ? "Tripped" : "Ok" },
    { label: "Ozone run time", icon: <AccessTimeOutlinedIcon sx={{ color: ICON_COLORS.time }}  />, value: toHoursAndMinutes(r2[30]) + ":00" },
    { label: "H.SIG input", icon: <SettingsInputHdmiOutlinedIcon sx={{ color: ICON_COLORS.power }} />, value: r3.length >= 29 ? (r3[28] === "1" ? "On" : "Off") : "OFF" }
  ];

  // ELEMENTS
  const Elements = [
    {
      label: "Element 1 level (sm)",
      icon: <StraightenOutlinedIcon sx={{ color: ICON_COLORS.level }} />,
      value: r3[18] === "200" ? "On" : (r3[18] === "0" ? "Off" : r3[18])
    },
    {
      label: "Element 2 level (lg)",
      icon: <StraightenOutlinedIcon sx={{ color: ICON_COLORS.level }} />,
      value: r3[25] === "200" ? "On" : (r3[25] === "0" ? "Off" : r3[25])
    },
    {
      label: "Element current",
      icon: <StayCurrentLandscapeOutlinedIcon sx={{ color: ICON_COLORS.power }} />,
      value: (r3[22] / 10).toFixed(1)
    }
  ];

  // FILTRATION
  const Filtration = [
    { label: "Awake mins left", icon: <AccessTimeOutlinedIcon sx={{ color: ICON_COLORS.time }} />, value: toHoursAndMinutes(r2[16]) + ":00" },
    { label: "Pump total run time", icon: <AccessTimeOutlinedIcon sx={{ color: ICON_COLORS.time }} />, value: toHoursAndMinutes(r2[17]) + ":00" },
    { label: "Pump time per block", icon: <AccessTimeOutlinedIcon sx={{ color: ICON_COLORS.time }} />, value: toHoursAndMinutes(r2[18]) + ":00" }
  ];

  // VARI PUMP
  const VariPump = [
    {
      label: "Mode",
      icon: <SettingsBrightnessOutlinedIcon sx={{ color: ICON_COLORS.cool }} />,
      value: ["Vari", "1.SEQ", "2.SEQ", "3.SEQ", "Off", "Filt", "Heat", "Cool"][Number(r4[23])]
    },
    { label: "Speed level", icon: <SpeedOutlinedIcon sx={{ color: ICON_COLORS.speed }} />, value: r4[24] },
    { label: "Speed %", icon: <PercentOutlinedIcon sx={{ color: ICON_COLORS.level }} />, value: r4[25] }
  ];

  const renderTile = (item) => {
    const isOnGreen = item.value === "On" || item.value === "ON";
    const isTripped = item.value === "Tripped";

    const tileBg =
      isTripped
        ? "rgba(244, 67, 54, 0.22)"
        : "rgba(255,255,255,0.04)";

    const borderColor = "rgba(255,255,255,0.1)";

    const iconBg =
      isTripped
        ? "rgba(244,67,54,0.9)"
        : isOnGreen
          ? "success.main"
          : "rgba(255,255,255,0.05)";

    const iconColor =
      isTripped || isOnGreen ? "#000" : "rgba(255,255,255,0.7)";

    const valueColor =
      isTripped
        ? "rgba(244,67,54,1)"
        : isOnGreen
          ? "success.main"
          : "rgba(255,255,255,0.6)";

    return (
      <Grid item xs={12} sm={6} md={3} key={item.label}>
        <Paper
          sx={{
            p: 1.2,
            minHeight: {
              xs: 140,
              sm: 'auto',
            },
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            borderRadius: 2,
            bgcolor: tileBg,
            border: `1px solid ${borderColor}`,
            transition: "0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              bgcolor:
                isTripped
                  ? "rgba(244,67,54,0.32)"
                  : isOnGreen
                    ? "rgba(56,142,60,0.32)"
                    : "rgba(255,255,255,0.08)",
            },
          }}
        >
          {/* Icon bubble */}
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconBg,
              color: iconColor,
              flexShrink: 0,
            }}
          >
            {item.icon}
          </Box>

          {/* Label + value */}
          <Box sx={{
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.3,
            mt: 0.4
          }}>
            <Typography
              color="white"
              sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1, }}
            >
              {item.label}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: 13,
              }}
            >
              {item.value}
            </Typography>
          </Box>
        </Paper>
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <Typography
        component="h2"
        variant="h6"
        color="white"
        gutterBottom
        sx={{ fontWeight: 600 }}
      >
        Control
      </Typography>

      <Box sx={{ width: "100%" }}>
        <Grid container spacing={3}>

          {/* GENERAL */}
          <Grid item xs={12}>
            <Typography sx={{ color: "white", fontWeight: 600, mb: 1 }}>General</Typography>
            <Grid container spacing={1.5}>
              {General.map(renderTile)}
            </Grid>
          </Grid>

          {/* ELEMENTS */}
          <Grid item xs={12}>
            <Typography sx={{ color: "white", fontWeight: 600, mb: 1 }}>Elements</Typography>
            <Grid container spacing={1.5}>
              {Elements.map(renderTile)}
            </Grid>
          </Grid>

          {/* FILTRATION */}
          <Grid item xs={12}>
            <Typography sx={{ color: "white", fontWeight: 600, mb: 1 }}>Filtration</Typography>
            <Grid container spacing={1.5}>
              {Filtration.map(renderTile)}
            </Grid>
          </Grid>

          {/* VARI PUMP */}
          <Grid item xs={12}>
            <Typography sx={{ color: "white", fontWeight: 600, mb: 1 }}>Vari Pump</Typography>
            <Grid container spacing={1.5}>
              {VariPump.map(renderTile)}
            </Grid>
          </Grid>

        </Grid>
      </Box>
    </React.Fragment>
  );
}
